import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Search, Mail, Loader2 } from "lucide-react";
import { MemberStatus } from "@satyrsmc/shared/lib/enums";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface EmailRosterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Club-wide distribution list the roster goes to when "all active members" is chosen. */
const ALL_MEMBERS_EMAIL = "allsatyrs@satyrsmc.org";

type Recipient = { id: string; name: string; email: string | null };

export function EmailRosterDialog({ open, onOpenChange }: EmailRosterDialogProps) {
  const [allActive, setAllActive] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: members } = trpc.admin.members.list.useQuery(undefined, {
    enabled: open,
  });

  // Active members only, sorted by name.
  const activeMembers = useMemo<Recipient[]>(() => {
    return (members ?? [])
      .filter((m) => m.status === MemberStatus.Active)
      .map((m) => ({ id: m.id, name: m.name, email: m.email ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeMembers;
    return activeMembers.filter((m) => m.name.toLowerCase().includes(q));
  }, [activeMembers, query]);

  const emailRoster = trpc.admin.roster.emailRoster.useMutation({
    onSuccess: ({ sent, skipped }, variables) => {
      if (variables.all_active_members) {
        toast.success(`Roster emailed to the all-members list (${ALL_MEMBERS_EMAIL})`);
      } else {
        const base = `Roster emailed to ${sent} member${sent === 1 ? "" : "s"}`;
        toast.success(skipped > 0 ? `${base} · ${skipped} skipped (no email)` : base);
      }
      handleOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  function toggleMember(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setAllActive(false);
      setSelectedIds(new Set());
      setQuery("");
      setPickerOpen(false);
    }
    onOpenChange(next);
  }

  const canSend = !emailRoster.isPending && (allActive || selectedIds.size > 0);

  function handleSend() {
    emailRoster.mutate({
      all_active_members: allActive,
      member_ids: allActive ? [] : Array.from(selectedIds),
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Email Roster</DialogTitle>
          <DialogDescription>
            Send a PDF copy of the club roster. Choose the all-members list, or pick individual
            members to email separately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* All active members toggle */}
          <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={allActive}
              onChange={(e) => setAllActive(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">Send to all active members</span>
              <span className="block text-muted-foreground">
                Sends one email to the all-members list ({ALL_MEMBERS_EMAIL})
              </span>
            </span>
          </label>

          {/* Member multi-select. Rendered inline (not a portaled Popover) so it
              lives inside the Dialog's DOM — otherwise the dialog's scroll-lock
              blocks wheel scrolling on the list. */}
          <div className={allActive ? "pointer-events-none opacity-50" : ""}>
            <p className="mb-1.5 text-sm font-medium">Select members</p>
            <button
              type="button"
              disabled={allActive}
              onClick={() => setPickerOpen((o) => !o)}
              className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm"
              aria-label="Select members"
              aria-expanded={pickerOpen && !allActive}
            >
              <span className={selectedIds.size ? "" : "text-muted-foreground"}>
                {selectedIds.size
                  ? `${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"} selected`
                  : "Choose members…"}
              </span>
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  pickerOpen && !allActive ? "rotate-180" : ""
                }`}
              />
            </button>

            {pickerOpen && !allActive && (
              <div className="mt-2 rounded-md border">
                <div className="relative border-b p-2">
                  <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search members…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto py-1">
                  {filtered.length === 0 ? (
                    <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                      No members found.
                    </p>
                  ) : (
                    filtered.map((m) => {
                      const disabled = !m.email;
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-3 px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-muted-foreground"
                              : "cursor-pointer hover:bg-muted/50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="size-4"
                            checked={selectedIds.has(m.id)}
                            disabled={disabled}
                            onChange={() => toggleMember(m.id)}
                          />
                          <span className="flex-1">{m.name}</span>
                          {disabled && <span className="text-xs italic">(no email)</span>}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
            {emailRoster.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Mail className="size-4" />
            )}
            {emailRoster.isPending
              ? "Sending…"
              : allActive
                ? "Send to all members"
                : `Send${selectedIds.size ? ` to ${selectedIds.size}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
